import styled from '@emotion/styled';
import { Typography } from '@mui/material';
import { useState } from 'react';
import useRouteId from '@/common/hooks/use-route-id';
import { TagDisplayCard } from '@/components/cards/tag-display-card/tag-display-card';
import {
  ExperienceContent,
  Tag,
  TagCategory,
  useGetExperienceTagsQuery,
} from '@/generated/graphql';
import { AccessIcon, SettingIcon, WhatIcon } from './propped-icons';
import TabButton from './tab-button';

const MainDiv = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: center;
  width: 100%;
  height: auto;
  min-height: 200px;
  border-radius: 25px;
  background: #4c3b51;
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
  padding: 0 12.5%;
  position: relative;
  z-index: 11;
`;
const ButtonsGroup = styled.div`
  height: 65px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  background: ${({ theme }) => theme.palette.common.cream};
  border-radius: 64px;
  padding: 9px;
  width: 100%;
  margin-left: 12.5%;
  margin-right: 12.5%;
  margin-bottom: 24px;
  margin-top: -32.5px;
  column-gap: 20px;
`;
const ButtonDiv = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
`;
const AnswerText = styled(Typography)`
  font-family: var(--font-work-sans);
  font-size: 19px;
  line-height: 160%;
  color: #ffffff;
  @media (max-width: 768px) {
    margin-bottom: 32px;
  }
`;

const TagsDiv = styled.div`
  width: 100%;
  height: auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-auto-rows: 1fr;
  gap: 32px;
  row-gap: 64px;
  position: relative;
  z-index: 2;
  padding-bottom: 32px;
`;

export type TabbedResponseProps = {
  content: ExperienceContent;
};
const TabbedResponseSelector = ({ content }: TabbedResponseProps) => {
  const [index, setIndex] = useState<number>(0);
  const experienceId = useRouteId();
  const experienceTagsQuery = useGetExperienceTagsQuery({
    variables: {
      id: experienceId,
    },
  });
  const tags = experienceTagsQuery.data?.experience?.experienceTags?.edges?.map(experienceTag => {
    return {
      id: experienceTag.node.tag!.id,
      title: experienceTag.node.tag!.title,
      category: experienceTag.node.tag!.category,
      description: experienceTag.node.tag!.description,
      icon: experienceTag.node.tag!.icon,
    };
  });
  const tagsExist = tags && tags?.length > 0;
  const accessibilityTags = tags?.filter(tag => tag.category === TagCategory.Accessibility);
  const accessibilityText = content.accessibility;
  const whatToBringTags = tags?.filter(tag => tag.category === TagCategory.WhatToBring);
  const whatToBringText = content.bring;
  const settingTags = tags?.filter(tag => tag.category === TagCategory.Setting);
  const settingText = content.environment;
  return (
    <MainDiv id="accessibility">
      <ButtonsGroup>
        <ButtonDiv>
          <TabButton
            isActive={index === 0 ? true : false}
            onClick={() => setIndex(0)}
            size="medium"
            icon={<AccessIcon isFocused={index === 0 ? true : false} />}
          >
            {'Accessibility'}
          </TabButton>
        </ButtonDiv>
        <ButtonDiv>
          <TabButton
            isActive={index === 1 ? true : false}
            onClick={() => setIndex(1)}
            size="medium"
            icon={<WhatIcon isFocused={index === 1 ? true : false} />}
          >
            {'What to Bring'}
          </TabButton>
        </ButtonDiv>
        <ButtonDiv>
          <TabButton
            isActive={index === 2 ? true : false}
            onClick={() => setIndex(2)}
            size="medium"
            icon={<SettingIcon isFocused={index === 2 ? true : false} />}
          >
            {'The Setting'}
          </TabButton>
        </ButtonDiv>
      </ButtonsGroup>
      {tagsExist ? (
        <>
          <TagsDiv style={index === 0 ? { display: 'grid' } : { display: 'none' }}>
            {accessibilityTags &&
              accessibilityTags.map(
                tag =>
                  tag && (
                    <TagDisplayCard
                      key={tag.id}
                      tag={{
                        title: tag.title!,
                        description: tag.description!,
                        icon: tag.icon!,
                      }}
                    />
                  ),
              )}
          </TagsDiv>
          <TagsDiv style={index === 1 ? { display: 'grid' } : { display: 'none' }}>
            {whatToBringTags &&
              whatToBringTags.map(
                tag =>
                  tag && (
                    <TagDisplayCard
                      key={tag.id}
                      tag={{
                        title: tag.title!,
                        description: tag.description!,
                        icon: tag.icon!,
                      }}
                    />
                  ),
              )}
          </TagsDiv>
          <TagsDiv style={index === 2 ? { display: 'grid' } : { display: 'none' }}>
            {settingTags &&
              settingTags.map(
                tag =>
                  tag && (
                    <TagDisplayCard
                      key={tag.id}
                      tag={{
                        title: tag.title!,
                        description: tag.description!,
                        icon: tag.icon!,
                      }}
                    />
                  ),
              )}
          </TagsDiv>
        </>
      ) : (
        <>
          <AnswerText style={index === 0 ? { display: 'block' } : { display: 'none' }}>
            {content.accessibility}
          </AnswerText>
          <AnswerText style={index === 1 ? { display: 'block' } : { display: 'none' }}>
            {content.bring}
          </AnswerText>
          <AnswerText style={index === 2 ? { display: 'block' } : { display: 'none' }}>
            {content.environment}
          </AnswerText>
        </>
      )}
    </MainDiv>
  );
};

export default TabbedResponseSelector;
